import { Button, DateTimePicker, Modal, ModalFooter } from '@/components/common';
import { RfidTagListItem, UserDTO } from '@/models';
import React, { useMemo, useState } from 'react';

export interface RfidTagAssignModalProps {
  isOpen: boolean;
  tag: RfidTagListItem | null;
  users: UserDTO[];
  isLoadingUsers: boolean;
  isAssigning: boolean;
  onConfirm: (userId: string, expiresAt?: string) => Promise<void> | void;
  onClose: () => void;
}

export const RfidTagAssignModal: React.FC<RfidTagAssignModalProps> = ({
  isOpen,
  tag,
  users,
  isLoadingUsers,
  isAssigning,
  onConfirm,
  onClose,
}) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [search, setSearch] = useState('');

  // Reset internal state whenever the modal opens for a new tag.
  React.useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
      setExpiresAt(null);
      setSearch('');
    }
  }, [isOpen, tag?.cardId]);

  // Scope the user list to the tag's institution so cross-institution assignment is impossible
  // from the UI (the backend still enforces this).
  const institutionUsers = useMemo(() => {
    if (!tag) return [];
    return users.filter((u) => u.institutionId === tag.institutionId);
  }, [users, tag]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return institutionUsers;
    return institutionUsers.filter((u) => {
      const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [institutionUsers, search]);

  const handleConfirm = async () => {
    if (!selectedUserId) return;
    await onConfirm(selectedUserId, expiresAt ? expiresAt.toISOString() : undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title="Link RFID tag to user"
      size="md"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleConfirm}
          confirmText="Assign"
          isLoading={isAssigning}
          disabled={!selectedUserId}
        />
      }
    >
      {tag && (
        <div className="mb-3">
          <label className="form-label small text-muted mb-1">Tag serial</label>
          <div className="font-monospace">{tag.serialNumber}</div>
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="rfid-assign-user-search" className="form-label">
          Search user
        </label>
        <input
          id="rfid-assign-user-search"
          type="text"
          className="form-control form-control-sm"
          placeholder="Name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="rfid-assign-user" className="form-label">
          User <span className="text-danger">*</span>
        </label>
        <select
          id="rfid-assign-user"
          className="form-select"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          disabled={isLoadingUsers || filteredUsers.length === 0}
          size={Math.min(8, Math.max(4, filteredUsers.length))}
        >
          {filteredUsers.length === 0 && !isLoadingUsers && (
            <option value="" disabled>
              No users found
            </option>
          )}
          {filteredUsers.map((u) => {
            const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
            const label = fullName ? `${fullName} (${u.email ?? '—'})` : (u.email ?? u.id);
            return (
              <option key={u.id} value={u.id}>
                {label}
              </option>
            );
          })}
        </select>
        {isLoadingUsers && <small className="text-muted">Loading users...</small>}
      </div>

      <div className="mb-3">
        <label className="form-label">Expires at (optional)</label>
        <DateTimePicker value={expiresAt} onChange={setExpiresAt} size="sm" />
        {expiresAt && (
          <Button
            variant="link"
            size="sm"
            className="p-0 mt-1"
            onClick={() => setExpiresAt(null)}
          >
            Clear
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default RfidTagAssignModal;
